import { useCallback, useEffect, useState } from "react";
import { Loader2, ArrowRight, Plus, X, Plane, HardHat, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const euro = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—");

const STATUS_LABELS = { a_facturer: "À facturer", facturee: "Facturée", a_payer: "À payer", payee: "Payée", encaissee: "Encaissée", annulee: "Annulée", avoir: "Avoir" };
const STATUS_CLS = {
  a_facturer: "bg-slate-100 text-slate-500", facturee: "bg-sky-100 text-sky-700", a_payer: "bg-amber-100 text-amber-700",
  payee: "bg-indigo-100 text-indigo-700", encaissee: "bg-sage/20 text-navy", annulee: "bg-red-100 text-red-600", avoir: "bg-red-100 text-red-600",
};
const NEXT_STEP = { a_facturer: "facturee", facturee: "a_payer", a_payer: "payee", payee: "encaissee" };

const NewCommissionModal = ({ partnerCompanyId, onClose, onCreated }) => {
  const [form, setForm] = useState({ project_id: "", company_id: "", lot_id: "", amount_ht: "", origin: "partner", reason: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.project_id || !form.company_id || !form.amount_ht || !form.reason.trim()) {
      toast.error("Projet, entreprise, montant HT et motif sont requis"); return;
    }
    setSaving(true);
    try {
      await api.post("/phase3/admin/commissions", {
        project_id: form.project_id, company_id: form.company_id, lot_id: form.lot_id || null,
        amount_ht: Number(form.amount_ht), origin: form.origin, reason: form.reason,
      });
      toast.success("Commission créée manuellement");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/70 p-6 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="new-commission-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-7">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-medium text-navy">Créer un marché (régularisation)</h3>
          <button data-testid="new-commission-close" onClick={onClose} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-navy hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <input data-testid="new-commission-project" placeholder="Référence projet (ex: VSL01-26-014)" value={form.project_id}
            onChange={(e) => setForm({ ...form, project_id: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="new-commission-company" placeholder="ID entreprise (ex: co_pr1)" value={form.company_id}
            onChange={(e) => setForm({ ...form, company_id: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="new-commission-lot" placeholder="ID lot (optionnel)" value={form.lot_id}
            onChange={(e) => setForm({ ...form, lot_id: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="new-commission-amount" type="number" placeholder="Montant HT du marché" value={form.amount_ht}
            onChange={(e) => setForm({ ...form, amount_ht: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <select data-testid="new-commission-origin" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none">
            <option value="partner">Entreprise apportée par le partenaire (75/25)</option>
            <option value="network">Entreprise trouvée par le réseau (100% Centrale)</option>
          </select>
          <textarea data-testid="new-commission-reason" placeholder="Motif de la création manuelle" value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
        </div>
        <button data-testid="new-commission-submit" disabled={saving} onClick={submit}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Créer la commission
        </button>
      </div>
    </div>
  );
};

const ProgressBar = ({ value, max }) => (
  <div className="h-3 w-full overflow-hidden rounded-full bg-cream">
    <div className="h-full rounded-full bg-gradient-to-r from-cognac to-cognac-light transition-all duration-500" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
  </div>
);

export const PartnerFiche = () => {
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  const [year, setYear] = useState(2026);
  const [summary, setSummary] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/phase3/admin/commission-partners"),
      api.get("/phase3/admin/commissions-overview"),
    ]).then(([partnersRes, overviewRes]) => {
      setPartners(partnersRes.data);
      const topPartnerId = overviewRes.data.by_partner[0]?.partner_company_id;
      const defaultId = partnersRes.data.some((p) => p.id === topPartnerId) ? topPartnerId : partnersRes.data[0]?.id;
      if (defaultId) setPartnerId(defaultId);
    }).catch(() => toast.error("Impossible de charger les partenaires"));
  }, []);

  const fetchAll = useCallback(() => {
    if (!partnerId) return;
    setLoading(true);
    Promise.all([
      api.get(`/phase3/admin/partners/${partnerId}/commission-summary`, { params: { year } }),
      api.get("/phase3/admin/commissions", { params: { partner_company_id: partnerId, year } }),
    ]).then(([s, c]) => { setSummary(s.data); setCommissions(c.data); })
      .catch(() => toast.error("Impossible de charger la fiche partenaire"))
      .finally(() => setLoading(false));
  }, [partnerId, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const advance = async (commission) => {
    const next = NEXT_STEP[commission.status];
    if (!next) return;
    setBusyId(commission.id);
    try {
      await api.patch(`/phase3/admin/commissions/${commission.id}/status`, { status: next, reason: "Progression manuelle Centrale" });
      toast.success(`Commission passée à « ${STATUS_LABELS[next]} »`);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="partner-fiche">
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="partner-fiche-select" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-bold text-navy focus:border-cognac focus:outline-none">
          {partners.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
        </select>
        <select data-testid="partner-fiche-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-bold text-navy focus:border-cognac focus:outline-none">
          {[2026, 2027, 2025].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button data-testid="open-new-commission" onClick={() => setShowModal(true)}
          className="ml-auto flex items-center gap-1.5 rounded-full bg-navy px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac">
          <Plus className="h-3.5 w-3.5" /> Nouveau marché
        </button>
      </div>

      {loading || !summary ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>
      ) : (
        <>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Commissions Centrale générées en {year}</p>
            <p className="mt-2 font-display text-3xl font-medium text-navy" data-testid="partner-fiche-centrale-total">
              {euro(summary.progress.centrale_total)} {summary.progress.next_threshold ? `/ ${euro(summary.progress.next_threshold)}` : ""}
            </p>
            {summary.progress.next_threshold && (
              <>
                <div className="mt-3"><ProgressBar value={summary.progress.centrale_total} max={summary.progress.next_threshold} /></div>
                <p className="mt-2 text-sm text-slate-500">
                  Prochain palier : <span className="font-semibold text-navy">{euro(summary.progress.next_threshold)}</span> —
                  Reste à générer : <span className="font-semibold text-navy">{euro(summary.progress.remaining_to_next_threshold)}</span>
                </p>
              </>
            )}
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div data-testid="fiche-material" className="rounded-2xl border border-slate-200 bg-white p-5">
              <HardHat className="h-4 w-4 text-cognac" />
              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Avantage matériel</p>
              <p className="mt-1 font-display text-xl font-medium text-navy">{euro(summary.progress.material.available)} disponible</p>
              <p className="mt-1 text-xs text-slate-500">Budget {euro(summary.progress.material.budget_total)} — consommé {euro(summary.progress.material.consumed)}</p>
            </div>
            <div data-testid="fiche-travel" className="rounded-2xl border border-slate-200 bg-white p-5">
              <Plane className="h-4 w-4 text-cognac" />
              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Voyage CozyHome</p>
              <p className="mt-1 font-display text-xl font-medium text-navy">
                {summary.progress.travel.qualified ? `${summary.progress.travel.label} — ${summary.progress.travel.partner_pct}% à charge` : "Non qualifié"}
              </p>
              {summary.progress.travel.next_threshold && (
                <p className="mt-1 text-xs text-slate-500">
                  À {euro(summary.progress.travel.next_threshold)}, participation à {summary.progress.travel.next_partner_pct}%
                </p>
              )}
            </div>
            <div data-testid="fiche-bonus" className="rounded-2xl border border-slate-200 bg-white p-5">
              <Trophy className="h-4 w-4 text-cognac" />
              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Prime Excellence</p>
              <p className="mt-1 font-display text-xl font-medium text-navy">
                {summary.progress.bonus.unlocked ? `${euro(summary.progress.bonus.amount)} débloquée` : `Débloquée à ${euro(summary.progress.bonus.threshold)}`}
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              ["Marchés HT encaissés", euro(summary.total_marches_ht)],
              ["Commission totale", euro(summary.total_commission)],
              ["Part partenaire versée", euro(summary.total_partner_amount)],
              ["Part Centrale impayée", euro(summary.total_centrale_impaye)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="font-display text-lg font-semibold text-navy">{value}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <p className="border-b border-slate-100 px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Marchés de l'année</p>
            {commissions.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-slate-400">Aucun marché pour cette année.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-400">
                      <th className="px-5 py-3">Entreprise</th>
                      <th className="px-5 py-3">Origine</th>
                      <th className="px-5 py-3 text-right">Montant HT</th>
                      <th className="px-5 py-3 text-right">Commission</th>
                      <th className="px-5 py-3 text-right">Part Centrale</th>
                      <th className="px-5 py-3">Statut</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c) => (
                      <tr key={c.id} data-testid={`fiche-commission-row-${c.id}`} className="border-b border-slate-100 last:border-0">
                        <td className="px-5 py-3 font-semibold text-navy">{c.company_name}</td>
                        <td className="px-5 py-3 text-slate-500">{c.origin === "partner" ? "Partenaire (75/25)" : "Réseau (100%)"}</td>
                        <td className="px-5 py-3 text-right text-slate-600">{euro(c.amount_ht)}</td>
                        <td className="px-5 py-3 text-right text-slate-600">{euro(c.commission_total)}</td>
                        <td className="px-5 py-3 text-right font-semibold text-navy">{euro(c.centrale_amount)}</td>
                        <td className="px-5 py-3">
                          <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_CLS[c.status]}`}>{STATUS_LABELS[c.status]}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          {NEXT_STEP[c.status] && (
                            <button data-testid={`fiche-advance-${c.id}`} disabled={busyId === c.id} onClick={() => advance(c)}
                              className="flex items-center gap-1 rounded-full border border-navy/20 px-3 py-1.5 text-xs font-bold text-navy transition-colors hover:border-cognac hover:text-cognac disabled:opacity-50">
                              {STATUS_LABELS[NEXT_STEP[c.status]]} <ArrowRight className="h-3 w-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showModal && (
        <NewCommissionModal partnerCompanyId={partnerId} onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); fetchAll(); }} />
      )}
    </div>
  );
};
